import classNames from 'classnames';
import type { TFunction } from 'i18next';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, DisplayCreative, FacebookDisplayCreative } from '@feathr/blackbox';
import { isAdTagCreative } from '@feathr/blackbox';
import {
  AdTagPreview,
  EmptyState,
  FacebookNewsfeedPreview,
  FacebookStoryPreview,
  InstagramFeedPreview,
  InstagramStoryPreview,
  Spinner,
  useVideoFrameExtract,
} from '@feathr/components';

import { getCreativeThumbnailUrl, hasStoredThumbnail } from '../../Creatives.utils';
import type { TSocialPreviewType } from '../SocialPreviewTypeSelector';
import SocialPreviewTypeSelector from '../SocialPreviewTypeSelector';
import ZoomControls from '../ZoomControls';
import { usePreviewPanel } from './PreviewPanel.hooks';
import {
  buildTestClickUrl,
  formatAdTag,
  getAdTagPreviewSource,
  getBannersnackPreviewSource,
  getCtaLabel,
  shouldShowEmptyState,
} from './PreviewPanel.utils';

import * as styles from './PreviewPanel.css';

export interface IPreviewPanelProps {
  /** The creative model to preview. */
  creative?: DisplayCreative;
  /**
   * Optional campaign for click-through tracking.
   * When provided, ad tag previews become interactive and clicks are tracked.
   */
  campaign?: Campaign;
  className?: string;
}

const PLACEHOLDER_PAGE_NAME = 'Your Page';
const PLACEHOLDER_HEADLINE = 'Headline';
const PLACEHOLDER_WEBSITE_URL = 'example.com';

function renderSocialPreview(
  socialPreviewType: TSocialPreviewType,
  imageUrl: string | undefined,
  videoUrl: string | undefined,
  creative: FacebookDisplayCreative,
  t: TFunction,
): JSX.Element {
  const mediaProps = videoUrl ? { videoUrl } : { imageUrl: imageUrl! };
  const primaryText = creative.get('primary_text') || undefined;
  const headline = creative.get('headlines')?.[0] || PLACEHOLDER_HEADLINE;
  const websiteUrl = creative.get('display_link') || PLACEHOLDER_WEBSITE_URL;
  const ctaText = getCtaLabel(creative.get('call_to_action_type'));

  const commonProps = {
    ...mediaProps,
    pageName: PLACEHOLDER_PAGE_NAME,
    primaryText,
    ctaText,
    t,
  };

  switch (socialPreviewType) {
    case 'facebook_story':
      return <FacebookStoryPreview {...commonProps} />;

    case 'facebook_newsfeed':
      return (
        <FacebookNewsfeedPreview {...commonProps} headline={headline} websiteUrl={websiteUrl} />
      );

    case 'instagram_feed':
      return <InstagramFeedPreview {...commonProps} />;

    case 'instagram_story':
      return <InstagramStoryPreview {...commonProps} />;
  }
}

function PreviewPanel({
  creative,
  campaign,
  className,
}: Readonly<IPreviewPanelProps>): JSX.Element {
  const { t } = useTranslation();
  const [videoPlaybackStarted, setVideoPlaybackStarted] = useState(false);

  // Reset video playback state when switching between creatives
  useEffect(() => {
    setVideoPlaybackStarted(false);
  }, [creative?.id]);

  const isAdTag = creative ? isAdTagCreative(creative) : false;
  const isMetaCreative = creative?.channel === 'meta';
  const isVideo = creative?.creativeType === 'Video';
  const contentWidth = creative?.get('width');
  const contentHeight = creative?.get('height');
  const creativeName = creative?.name;

  const testClickUrl = creative && campaign ? buildTestClickUrl(creative, campaign) : undefined;

  const bannersnackPreview = getBannersnackPreviewSource(creative);

  const hasThumbnail = creative ? hasStoredThumbnail(creative) : false;
  const creativeUrl =
    !isAdTag && !bannersnackPreview && creative ? getCreativeThumbnailUrl(creative) : undefined;
  const needsVideoExtract = isVideo && !hasThumbnail && !!creativeUrl;
  const {
    thumbnailUrl: videoFrameUrl,
    isLoading: isVideoThumbnailLoading,
    hasError: hasVideoThumbnailError,
  } = useVideoFrameExtract(creativeUrl ?? '', needsVideoExtract);

  const imageUrl = isVideo && !hasThumbnail ? videoFrameUrl || undefined : creativeUrl;
  const videoUrl = isVideo ? creative?.get('url') ?? undefined : undefined;

  const {
    zoom,
    socialPreviewType,
    viewportRef,
    contentRef,
    handleZoomChange,
    handleZoomToFit,
    handleSocialPreviewTypeChange,
  } = usePreviewPanel({ contentWidth, contentHeight });

  function renderPreview(): JSX.Element {
    if (
      shouldShowEmptyState({
        imageUrl,
        videoUrl,
        isAdTag,
        hasBannersnackPreview: bannersnackPreview !== null,
        needsVideoExtract,
        isVideoThumbnailLoading,
        hasVideoThumbnailError,
      })
    ) {
      return (
        <EmptyState
          description={t('Upload an image to see a preview here.')}
          label={t('No preview available')}
        />
      );
    }

    const adTagSource = getAdTagPreviewSource(
      creative,
      isAdTag,
      bannersnackPreview?.kind === 'html' ? bannersnackPreview.html : undefined,
    );
    if (adTagSource) {
      const formattedHtml = testClickUrl
        ? formatAdTag(adTagSource.html, testClickUrl)
        : adTagSource.html;
      return (
        <AdTagPreview
          allowInteraction={!!testClickUrl}
          height={adTagSource.height}
          html={formattedHtml}
          // Key forces iframe remount when dimensions change (browsers cache srcDoc)
          key={`${adTagSource.width}x${adTagSource.height}`}
          scale={zoom / 100}
          width={adTagSource.width}
        />
      );
    }

    if (bannersnackPreview?.kind === 'image') {
      const bannersnackImage = (
        <img alt={creativeName} className={styles.image} src={bannersnackPreview.url} />
      );

      if (testClickUrl) {
        return (
          <a href={testClickUrl} rel={'noopener noreferrer'} target={'_blank'}>
            {bannersnackImage}
          </a>
        );
      }

      return bannersnackImage;
    }

    if (isMetaCreative && (imageUrl || videoUrl)) {
      return renderSocialPreview(
        socialPreviewType,
        imageUrl,
        videoUrl,
        creative as FacebookDisplayCreative,
        t,
      );
    }

    if (videoUrl) {
      const handleVideoPlay = (): void => setVideoPlaybackStarted(true);

      return (
        <div className={styles.videoContainer}>
          {videoPlaybackStarted && testClickUrl && (
            <a
              className={styles.videoClickZone}
              href={testClickUrl}
              rel={'noopener noreferrer'}
              target={'_blank'}
            >
              <span className={styles.hidden}>{t('Click to visit destination')}</span>
            </a>
          )}
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            className={styles.video}
            controls
            data-name={'video-preview'}
            onPlay={handleVideoPlay}
            src={videoUrl}
          />
        </div>
      );
    }

    const displayImage = (
      <>
        {needsVideoExtract && isVideoThumbnailLoading && <Spinner />}
        <img
          alt={creativeName}
          className={classNames(
            styles.image,
            needsVideoExtract && isVideoThumbnailLoading && styles.hidden,
          )}
          src={imageUrl}
        />
      </>
    );

    if (testClickUrl) {
      return (
        <a href={testClickUrl} rel={'noopener noreferrer'} target={'_blank'}>
          {displayImage}
        </a>
      );
    }

    return displayImage;
  }

  return (
    <div className={classNames(styles.root, className)}>
      <div className={styles.toolbar}>
        {isMetaCreative && (
          <SocialPreviewTypeSelector
            onChange={handleSocialPreviewTypeChange}
            value={socialPreviewType}
          />
        )}
        <ZoomControls
          className={styles.zoomControls}
          onZoomChange={handleZoomChange}
          onZoomToFit={handleZoomToFit}
          zoom={zoom}
        />
      </div>
      {/* tabIndex={0} makes the viewport focusable so the zoom keyboard shortcuts (Cmd/Ctrl +/-/0/1) only fire when the preview is focused, not anywhere on the page. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <div className={styles.viewport} ref={viewportRef} tabIndex={0}>
        {isAdTag ? (
          <div className={styles.adTagContainer} ref={contentRef}>
            {renderPreview()}
          </div>
        ) : (
          <div className={styles.content} ref={contentRef}>
            {renderPreview()}
          </div>
        )}
      </div>
    </div>
  );
}

export default observer(PreviewPanel);
